// pages/Works.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import TransitionLink from '../components/TransitionLink';
import { useWorks } from '../context/workConfig';
import { useWorksLoading } from '../hooks/useWorksLoading';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';
import SEO from '../components/SEO';

const Works = () => {
    const { projects } = useWorks();
    const [filteredProjects, setFilteredProjects] = useState([]);
    const { language } = useLanguage();
    const t = useTranslation(language);
    const itemsRef = useRef([]);
    const [isHoveringBack, setIsHoveringBack] = useState(false);
    const [isHoveringCode, setIsHoveringCode] = useState(false);
    const [isHoveringAbout, setIsHoveringAbout] = useState(false);

    // Works loading hook
    const {
        loadProject,
        loadMultipleProjects,
        isProjectLoaded,
        isProjectLoading,
        getProjectProgress
    } = useWorksLoading();

    const logoNames = ['Rai 1', 'La7', 'Dolce & Gabbana', 'ITS Apulia Digital Maker', 'Rai 2', 'La Repubblica', 'Rai News', 'CSC'];
    const realLogos = logoNames.map((name, index) => ({ src: `/img/logos/logo${index + 1}.png`, name }));

    useEffect(() => {
        if (window.lenis) {
            window.lenis.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo(0, 0);
        }
        setFilteredProjects(projects);

        // Start loading first batch of projects immediately
        if (projects.length > 0) {
            loadMultipleProjects(projects.slice(0, 6)); // Load first 6 projects
        }
    }, [projects, loadMultipleProjects]);

    // Intersection observer for lazy loading remaining projects
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const projectId = entry.target.dataset.projectId;
                        const project = projects.find(p => p.id === projectId);
                        if (project && !isProjectLoaded(projectId) && !isProjectLoading(projectId)) {
                            loadProject(project);
                        }
                    }
                });
            },
            { rootMargin: '200px' } // Start loading 200px before entering viewport
        );

        // Observe all project cards
        const projectCards = document.querySelectorAll('[data-project-id]');
        projectCards.forEach(card => observer.observe(card));

        return () => observer.disconnect();
    }, [projects, loadProject, isProjectLoaded, isProjectLoading]);

    const seoDescriptions = {
        en: "Broadcast, virtual production, fashion. Fifteen years of work for Italian national television, including RAI, LA7 and Dolce&Gabbana.",
        it: "Broadcast, produzione virtuale, moda. Quindici anni di lavoro per la televisione nazionale italiana, tra cui RAI, LA7 e Dolce&Gabbana."
    };

    const worksSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": language === 'it' ? "I Miei Lavori - Paolo Allegretti" : "My Works - Paolo Allegretti",
        "description": seoDescriptions[language],
        "url": "https://paoloallegretti.com/works",
        "author": { "@type": "Person", "name": "Paolo Allegretti" }
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO
                title={language === 'it' ? "I Miei Lavori" : "My Works"}
                description={seoDescriptions[language]}
                url="/works"
                language={language}
                schema={worksSchema}
            />
            <Header />

            <section className="py-32 pb-16 md:py-40 md:pb-6 relative">
                <div className="container mx-auto px-8 md:px-16">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-5xl md:text-8xl font-bold mb-8 tracking-tight"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                        {t('works_title')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-xl md:text-2xl max-w-2xl"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                        {t('works_desc')}
                    </motion.p>
                </div>
            </section>

            <section className="relative mb-16 overflow-hidden logo-marquee-section">
                <div className="absolute left-0 top-0 bottom-0 w-24 md:w-48 bg-gradient-to-r from-black to-transparent z-10"></div>
                <div className="absolute right-0 top-0 bottom-0 w-24 md:w-48 bg-gradient-to-l from-black to-transparent z-10"></div>
                <div className="py-8">
                    <div className="flex items-center space-x-16 animate-marquee-fast md:animate-marquee">
                        {realLogos.map((logo, index) => (
                            <div key={`logo-${index}`} className="flex-shrink-0">
                                <div className="h-16 w-32 rounded opacity-50 flex items-center justify-center text-gray-400 filter grayscale hover:grayscale-0 transition-all duration-300">
                                    <img
                                        src={logo.src}
                                        alt={logo.name}
                                        className="max-h-full max-w-full object-contain"
                                    />
                                </div>
                            </div>
                        ))}
                        {realLogos.map((logo, index) => (
                            <div key={`logo-dup-${index}`} className="flex-shrink-0">
                                <div className="h-16 w-32 rounded flex opacity-50   items-center justify-center text-gray-400 filter grayscale hover:grayscale-0 transition-all duration-300">
                                    <img
                                        src={logo.src}
                                        alt={logo.name}
                                        className="max-h-full max-w-full object-contain"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="pb-32">
                <div className="container mx-auto px-8 md:px-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredProjects.map((project, index) => (
                            <motion.div
                                key={project.id}
                                data-project-id={project.id}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="works-page-card relative"
                            >
                                <TransitionLink to={`/work/${project.id}`} className="block group project-content-area">
                                    <div className="rounded-xl overflow-hidden aspect-[4/3] mb-4 relative">
                                        {/* Loading state */}
                                        {isProjectLoading(project.id) && (
                                            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center z-10">
                                                <div className="text-center">
                                                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mb-2 mx-auto"></div>
                                                    <div className="text-xs text-gray-400">
                                                        {getProjectProgress(project.id)}%
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Project image */}
                                        <div
                                            className={`w-full h-full bg-cover bg-center transition-transform duration-500 group-hover:scale-105 ${isProjectLoaded(project.id) ? 'opacity-100' : 'opacity-30'
                                                }`}
                                            style={{ backgroundImage: `url(${project.mainImage})` }}
                                        />

                                        {/* Shimmer effect for loading */}
                                        {!isProjectLoaded(project.id) && !isProjectLoading(project.id) && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse"></div>
                                        )}
                                    </div>

                                    <div className="text-sm font-medium text-accent mb-2">{project.category}</div>
                                    <h3 className="text-2xl font-bold mb-2 transition-colors group-hover:text-accent">
                                        {t(project.title)}
                                    </h3>
                                    <p className="text-gray-400 mb-4">{t(project.description)}</p>
                                    <div className="flex items-center text-white group-hover:text-accent transition-colors">
                                        <span>{t('view_project')}</span>
                                        <svg className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </div>
                                </TransitionLink>
                            </motion.div>
                        ))}
                    </div>

                    <div
                        ref={(el) => (itemsRef.current[2] = el)}
                        className="flex justify-between items-start mt-16 mb-8"
                    >
                        <TransitionLink
                            to="/"
                            className="header-nav-link relative group flex items-center overflow-hidden"
                            onMouseEnter={() => setIsHoveringBack(true)}
                            onMouseLeave={() => setIsHoveringBack(false)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`mr-2 w-5 h-5 text-white group-hover:text-accent transition-all duration-500 ease-out transform ${isHoveringBack ? '-translate-x-1' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>

                            <div className="relative overflow-hidden px-1">
                                <span
                                    className="inline-block transform transition-transform duration-500 ease-out"
                                    style={{
                                        transform: isHoveringBack ? 'translateY(-100%)' : 'translateY(0)',
                                        fontFamily: "'Space Grotesk', 'Archivo', sans-serif",
                                        color: 'white',
                                        fontSize: '1.25rem',
                                        fontWeight: '500',
                                    }}
                                >
                                    {t('backHome')}
                                </span>
                                <span
                                    className="absolute top-full left-0 inline-block transform transition-transform duration-500 ease-out"
                                    style={{
                                        transform: isHoveringBack ? 'translateY(-100%)' : 'translateY(0)',
                                        fontFamily: "'Space Grotesk', 'Archivo', sans-serif",
                                        color: '#ffa500',
                                        fontSize: '1.25rem',
                                        fontWeight: '500',
                                    }}
                                >
                                    {t('backHome')}
                                </span>
                            </div>

                            <div className="absolute bottom-0 left-0 w-full h-[1px] bg-accent origin-left transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out"></div>
                        </TransitionLink>

                        <div className="flex flex-col items-end space-y-1 md:space-y-2">
                            {/* See All My Works Button */}
                            <TransitionLink
                                to="/code"
                                className="header-nav-link relative group flex items-center overflow-hidden"
                                onMouseEnter={() => setIsHoveringCode(true)}
                                onMouseLeave={() => setIsHoveringCode(false)}
                            >
                                <div className="absolute bottom-0 left-0 w-full h-[1px] bg-accent origin-left transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out"></div>

                                <div className="relative overflow-hidden px-1">
                                    <span
                                        className="inline-block transform transition-transform duration-500 ease-out text-sm md:text-xl whitespace-nowrap"
                                        style={{
                                            transform: isHoveringCode ? 'translateY(-100%)' : 'translateY(0)',
                                            fontFamily: "'Space Grotesk', 'Archivo', sans-serif",
                                            color: 'white',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {t('goToDeveloper')}
                                    </span>
                                    <span
                                        className="absolute top-full left-0 inline-block transform transition-transform duration-500 ease-out text-sm md:text-xl whitespace-nowrap"
                                        style={{
                                            transform: isHoveringCode ? 'translateY(-100%)' : 'translateY(0)',
                                            fontFamily: "'Space Grotesk', 'Archivo', sans-serif",
                                            color: '#ffa500',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {t('goToDeveloper')}
                                    </span>
                                </div>

                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`ml-1 md:ml-2 w-4 h-4 md:w-5 md:h-5 text-white group-hover:text-accent transition-all duration-500 ease-out transform ${isHoveringCode ? 'translate-x-1' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </TransitionLink>

                            {/* Download Resume Button */}
                            <TransitionLink
                                to="/about"
                                className="header-nav-link relative group flex items-center overflow-hidden"
                                onMouseEnter={() => setIsHoveringAbout(true)}
                                onMouseLeave={() => setIsHoveringAbout(false)}
                            >
                                <div className="relative overflow-hidden px-1">
                                    <span
                                        className="inline-block transform transition-transform duration-500 ease-out"
                                        style={{
                                            transform: isHoveringAbout ? 'translateY(-100%)' : 'translateY(0)',
                                            fontFamily: "'Space Grotesk', 'Archivo', sans-serif",
                                            color: 'white',
                                            fontSize: '1.25rem',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {t('aboutMe')}
                                    </span>
                                    <span
                                        className="absolute top-full left-0 inline-block transform transition-transform duration-500 ease-out"
                                        style={{
                                            transform: isHoveringAbout ? 'translateY(-100%)' : 'translateY(0)',
                                            fontFamily: "'Space Grotesk', 'Archivo', sans-serif",
                                            color: '#ffa500',
                                            fontSize: '1.25rem',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {t('aboutMe')}
                                    </span>
                                </div>

                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`ml-2 w-5 h-5 text-white group-hover:text-accent transition-all duration-500 ease-out transform ${isHoveringAbout ? 'translate-x-1' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>

                                <div className="absolute bottom-0 left-0 w-full h-[1px] bg-accent origin-left transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out"></div>
                            </TransitionLink>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Works;